import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { XIcon } from '@untitled-theme/icons-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { housePartsIcons } from '@/assets/icons/house-parts'
import { useUpdateHiddenRetrofits } from '@/shared/api/isfp-data.queries'
import Button from '@/shared/components/Button'
import CupertinoSwitch from '@/shared/components/CupertinoSwitch'
import TextButton from '@/shared/components/TextButton'
import { RetrofitNameSchema, type RetrofitName } from '@/shared/types/assessment.types'
import { retrofitDisplayNames, retrofitToIcon } from '@/shared/utils/displayMappings'

interface HiddenMeasuresDialogProps {
  open: boolean
  onClose: () => void
  availableMeasures: RetrofitName[]
  currentHiddenMeasures: RetrofitName[]
  buildingUuid: string
  onHiddenMeasureChange?: (hiddenMeasures: RetrofitName[]) => void
}

const HiddenMeasuresDialog = ({
  open,
  onClose,
  availableMeasures = RetrofitNameSchema.options,
  currentHiddenMeasures = [],
  buildingUuid,
  onHiddenMeasureChange,
}: HiddenMeasuresDialogProps) => {
  const [hiddenMeasures, setHiddenMeasures] = useState<RetrofitName[]>(currentHiddenMeasures)
  const [showHeaderShadow, setShowHeaderShadow] = useState(false)
  const [showFooterShadow, setShowFooterShadow] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Sync with prop changes
  useEffect(() => {
    if (!open) return
    setHiddenMeasures(currentHiddenMeasures)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Handle scroll to show/hide header and footer shadows
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
        setShowHeaderShadow(scrollTop > 0)
        setShowFooterShadow(scrollTop + clientHeight < scrollHeight - 1)
      }
    }

    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      // Check initial state
      handleScroll()
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scrollContainerRef.current])

  const { mutate: updateHiddenRetrofits, isPending } = useUpdateHiddenRetrofits({
    onSuccess: () => {
      toast.success('Überblick erfolgreich angepasst', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
      })
      onHiddenMeasureChange?.(hiddenMeasures)
      onClose()
    },
    onError: error => {
      toast.error(`Fehler beim Speichern: ${error.message || error}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
      })
    },
  })

  const handleToggleMeasure = (retrofitName: RetrofitName) => {
    setHiddenMeasures(prev => {
      if (prev.includes(retrofitName)) {
        return prev.filter(name => name !== retrofitName)
      }
      return [...prev, retrofitName]
    })
  }

  const handleSave = () => {
    updateHiddenRetrofits({
      buildingUuid,
      hiddenRetrofits: hiddenMeasures,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'white',
            borderRadius: '18px',
            maxWidth: '700px',
            width: '100%',
          },
        },
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 1,
          padding: '16px 24px',
          boxShadow: showHeaderShadow ? '0px 4px 16px rgba(0, 0, 0, 0.15)' : 'none',
          transition: 'box-shadow 0.2s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#181D29',
              }}
            >
              Überblick anpassen
            </Typography>
            <IconButton onClick={onClose} sx={{ padding: '12px', margin: '-12px' }}>
              <XIcon style={{ width: 24, height: 24, color: '#181D29' }} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: '#464A54',
            }}
          >
            Welche Maßnahmen sollen in dem Überblick angezeigt werden?
          </Typography>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box
        ref={scrollContainerRef}
        sx={{
          overflowY: 'auto',
          padding: '0 24px',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {[...availableMeasures]
          .sort((a, b) => retrofitDisplayNames[a].localeCompare(retrofitDisplayNames[b]))
          .map(retrofitName => {
            const isVisible = !hiddenMeasures.includes(retrofitName)
            const icon = retrofitToIcon[retrofitName]
            const displayName = retrofitDisplayNames[retrofitName]

            return (
              <Box
                key={retrofitName}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 0',
                  borderBottom: '1px solid #F0F0F0',
                }}
              >
                {icon && (
                  <Box
                    component="img"
                    src={housePartsIcons[icon]}
                    alt={displayName}
                    sx={{
                      width: 32,
                      height: 32,
                      objectFit: 'contain',
                      marginRight: '8px',
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#181D29',
                    flex: 1,
                  }}
                >
                  {displayName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#464A54',
                    marginRight: '24px',
                  }}
                >
                  Anzeigen
                </Typography>
                <CupertinoSwitch
                  checked={isVisible}
                  onChange={() => handleToggleMeasure(retrofitName)}
                />
              </Box>
            )
          })}
      </Box>

      {/* Sticky Footer */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 1,
          padding: '16px',
          display: 'flex',
          gap: '8px',
          boxShadow: showFooterShadow ? '0px -4px 16px rgba(0, 0, 0, 0.15)' : 'none',
          transition: 'box-shadow 0.2s ease',
        }}
      >
        <TextButton onClick={onClose} sx={{ flex: 1 }} disabled={isPending}>
          Abbrechen
        </TextButton>
        <Button onClick={handleSave} sx={{ flex: 1 }} disabled={isPending}>
          Speichern
        </Button>
      </Box>
    </Dialog>
  )
}

export default HiddenMeasuresDialog
